import { isValidElement } from 'react';
import type { ButtonProps, DisclaimerLink, Theme } from '../../../core/types';
import styles from './index.module.less';
import { Direction, Align, Size } from '../../../core/enums';
import Button from '../../archived/button';
import { Icon } from '../../atoms/icon';
import Link from '../../atoms/link';
import Element from '../../../core/Element';

interface GroupedButtonsProps {
  className?: string;
  data: {
    ctas?: ButtonProps<keyof React.ReactHTML | React.ElementType>[];
    disclaimerLinks: DisclaimerLink[];
    contentAfterCTA?: React.ReactNode;
  };
  customization?: {
    ctaContainerTheme?: Theme;
    disclaimerContainerTheme?: Theme;
    disclaimerItemTheme?: Theme;
    itemContainerTheme?: Theme;
    itemTheme?: Theme;
    evenCtaWidths?: boolean;
  };
  qeId?: string;
  $direction?: Direction;
  $isMinMax?: boolean;
  $textWrap?: boolean;
}

export const GroupedButtons = ({
  $direction,
  data: { ctas = [], disclaimerLinks, contentAfterCTA },
  className = '',
  customization: {
    ctaContainerTheme,
    disclaimerContainerTheme,
    disclaimerItemTheme,
    itemContainerTheme,
    itemTheme,
    evenCtaWidths,
  } = {},
  qeId = 'GroupedButtons',
  $isMinMax = true,
  $textWrap,
}: GroupedButtonsProps) => {
  const disclaimerContainerThemeWithDefaults = {
    paddingTop: 10,
    textAlign: Align.Center,
    ...disclaimerContainerTheme,
  };

  const isHorizontalDirection = $direction === Direction.Horizontal;

  const itemContainerThemeWithDefaults = (index: number) => {
    if (index > 0) {
      if (isHorizontalDirection) {
        return {
          marginLeft: 10,
          ...itemContainerTheme,
        };
      }
      return {
        paddingTop: 10,
        ...itemContainerTheme,
      };
    } else {
      return itemContainerTheme;
    }
  };

  return (
    <div className={className} data-qe-id={qeId}>
      <Element
        Component="span"
        className={
          $direction === Direction.Horizontal
            ? styles.sideBySide
            : styles.verticalCta
        }
        theme={ctaContainerTheme}
      >
        {ctas.map(({ text, url, theme, Component, ariaLabel, ...rest }, i) => {
          const mergedItemThemes = Object.assign(
            { horizontallyFluid: true },
            itemTheme,
            theme
          );
          const { icon, ...ctaProps } = rest;
          return (
            <Element
              key={i}
              className={
                evenCtaWidths
                  ? styles.itemContainerWithEvenWidth
                  : styles.itemContainer
              }
              theme={itemContainerThemeWithDefaults(i)}
            >
              {isValidElement(Component) ? (
                Component
              ) : (
                <Button
                  href={url}
                  Component={Component}
                  theme={mergedItemThemes}
                  $isMinMax={$isMinMax}
                  $textWrap={$textWrap}
                  aria-label={ariaLabel}
                  {...ctaProps}
                >
                  {/* this type of check is needed to support Event Tracking */}
                  {icon ? (
                    <>
                      <Icon Component="span" $size={Size.SM} {...icon} />
                      {text}
                    </>
                  ) : (
                    text
                  )}
                </Button>
              )}
            </Element>
          );
        })}
      </Element>

      {contentAfterCTA && contentAfterCTA}

      {disclaimerLinks && disclaimerLinks.length ? (
        <Element theme={disclaimerContainerThemeWithDefaults}>
          <ul className={styles.ulContainer}>
            {disclaimerLinks.map(
              (
                {
                  url,
                  text,
                  tools,
                  target,
                  theme,
                  ariaLabel,
                  srOnly,
                  ...linkProps
                },
                i
              ) => {
                const mergedTheme = Object.assign(
                  {
                    textLegal: 1,
                  },
                  disclaimerItemTheme,
                  theme
                );
                return (
                  <li
                    className={styles.liContainer}
                    key={`${text}-${url}-${i}`}
                  >
                    <Link
                      Component="a"
                      target={target}
                      href={url}
                      theme={mergedTheme}
                      tools={tools}
                      aria-label={ariaLabel}
                      className={styles.link}
                      {...linkProps}
                    >
                      {text}
                      {srOnly && <span className="sr-only">{srOnly}</span>}
                    </Link>
                  </li>
                );
              }
            )}
          </ul>
        </Element>
      ) : null}
    </div>
  );
};
